import React, { useEffect, useState } from "react";
import StatusBadge from "../components/StatusBadge";
import { API_URL } from "../api/config";

// Eye icon SVG component
function EyeIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M1.458 12C2.732 7.943 6.523 5 12 5c5.477 0 9.268 2.943 10.542 7-1.274 4.057-5.065 7-10.542 7-5.477 0-9.268-2.943-10.542-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export default function MobileOrderTable() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/orders.php`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        setOrders(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const getImageUrl = (image_path) => {
    if (!image_path) return null;
    if (image_path.startsWith("http")) return image_path;
    return `${process.env.NODE_ENV === "production" ? "https://teenagefashion.in" : "http://localhost/newfashion"}/${image_path}`;
  };

  // Filter orders by customer name (case-insensitive)
  const filteredOrders = orders.filter(order =>
    (order.customer_name || "").toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex justify-center items-center h-screen">Loading...</div>;

  return (
    <div className="p-2 md:p-6 max-w-md md:max-w-3xl mx-auto w-full">
      <h1 className="text-xl md:text-2xl font-bold mb-4 text-center">Orders</h1>
      <div className="mb-3">
        <input
          type="text"
          placeholder="Search by customer name"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div className="overflow-x-auto rounded shadow bg-white">
        <table className="min-w-full text-sm md:text-base">
          <thead>
            <tr className="bg-gray-100 text-gray-700">
              <th className="py-2 px-2 md:py-3 md:px-4 text-left">Image</th>
              <th className="py-2 px-2 md:py-3 md:px-4 text-left">Customer</th>
              <th className="py-2 px-2 md:py-3 md:px-4 text-left hidden sm:table-cell">Address</th>
              <th className="py-2 px-2 md:py-3 md:px-4 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center py-8 text-gray-400">
                  <div className="text-4xl mb-2">📦</div>
                  <div className="text-base">No orders found</div>
                </td>
              </tr>
            ) : (
              filteredOrders.map(order => (
                <tr key={order.id} className="border-b last:border-b-0">
                  <td className="py-2 px-2 md:py-3 md:px-4">
                    {order.image_path ? (
                      <img
                        src={getImageUrl(order.image_path)}
                        alt="Order"
                        className="w-12 h-12 md:w-20 md:h-20 object-cover rounded border cursor-pointer transition-transform duration-150 hover:scale-105"
                        loading="lazy"
                        onClick={() => setPreviewUrl(getImageUrl(order.image_path))}
                      />
                    ) : (
                      <div className="w-12 h-12 md:w-20 md:h-20 flex items-center justify-center bg-gray-100 rounded border text-gray-400 text-xl md:text-2xl">?</div>
                    )}
                  </td>
                  <td className="py-2 px-2 md:py-3 md:px-4 font-medium text-gray-900 truncate max-w-[120px] md:max-w-[220px]">
                    <div>{order.customer_name}</div>
                    {order.customer_phone && (
                      <div className="mt-1 inline-block bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">
                        {order.customer_phone}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-2 md:py-3 md:px-4 text-gray-700 truncate max-w-[120px] md:max-w-[220px] hidden sm:table-cell">
                    {order.customer_address || "-"}
                  </td>
                  <td className="py-2 px-2 md:py-3 md:px-4 flex items-center gap-2">
                    <button
                      className="p-1 rounded hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      title="View order details"
                      onClick={() => { setSelectedOrder(order); setShowOrderModal(true); }}
                    >
                      <EyeIcon className="w-5 h-5 text-gray-500" />
                    </button>
                    <StatusBadge status={order.status} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Image Preview Modal */}
      {previewUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80"
          onClick={() => setPreviewUrl(null)}
        >
          <div className="relative max-w-xs md:max-w-lg w-full p-2" onClick={e => e.stopPropagation()}>
            <button
              className="absolute top-2 right-2 text-white text-2xl bg-black bg-opacity-40 rounded-full w-8 h-8 flex items-center justify-center focus:outline-none"
              onClick={() => setPreviewUrl(null)}
              aria-label="Close preview"
            >
              &times;
            </button>
            <img
              src={previewUrl}
              alt="Preview"
              className="w-full h-auto max-h-[80vh] rounded shadow-lg border-2 border-white"
            />
          </div>
        </div>
      )}
      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60" onClick={() => setShowOrderModal(false)}>
          <div className="bg-white rounded-xl shadow-lg max-w-sm w-full mx-2 p-6 relative animate-fadeIn" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setShowOrderModal(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none"
              aria-label="Close"
            >
              ×
            </button>
            <h2 className="text-lg font-bold mb-2 text-gray-900">Order #{selectedOrder.id}</h2>
            <div className="mb-2 text-gray-700 text-sm">
              <div><span className="font-medium">Customer:</span> {selectedOrder.customer_name}</div>
              <div><span className="font-medium">Phone:</span> {selectedOrder.customer_phone}</div>
              <div><span className="font-medium">Address:</span> {selectedOrder.customer_address}</div>
              <div><span className="font-medium">Status:</span> <StatusBadge status={selectedOrder.status} /></div>
              <div><span className="font-medium">Delivery Date:</span> {selectedOrder.delivery_date}</div>
              <div><span className="font-medium">Total Amount:</span> ₹{selectedOrder.total_amount}</div>
              <div><span className="font-medium">Paid:</span> ₹{selectedOrder.paid}</div>
              <div><span className="font-medium">Balance:</span> ₹{selectedOrder.balance}</div>
              <div><span className="font-medium">Quantity:</span> {selectedOrder.quantity}</div>
              <div><span className="font-medium">Fabric Type:</span> {selectedOrder.fabric_type}</div>
              <div><span className="font-medium">Color:</span> {selectedOrder.color}</div>
              <div><span className="font-medium">Note:</span> {selectedOrder.note}</div>
            </div>
            {selectedOrder.image_path && (
              <img
                src={getImageUrl(selectedOrder.image_path)}
                alt="Order"
                className="w-full h-auto max-h-48 rounded border mt-2"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
} 